import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StudentLayout from "@/components/StudentLayout";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, BookOpen, Image as ImageIcon, Video as VideoIcon, Headphones,
  Mic, MessageSquare, Loader2, Maximize2, Minimize2, RotateCcw, X,
} from "lucide-react";
import { toast } from "sonner";
import { StatusPill, Bubble, VoiceComposer, ChatComposer, UnifiedComposer, SpeedPicker, useTuitionSession } from "@/pages/tuition/TuitionChatParts";
import { PdfViewer } from "@/components/PdfViewer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TuitionChat() {
  const { subjectId, lessonId } = useParams();
  const navigate = useNavigate();

  const [subject, setSubject] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content, ttsUrl?}
  const [mode, setMode] = useState("voice");    // "voice" | "chat"
  // Playback speed for tutor TTS — persisted in localStorage so a student's
  // chosen speed survives reloads.
  const [speed, setSpeed] = useState(() => {
    if (typeof window === "undefined") return 1;
    const v = parseFloat(window.localStorage.getItem("tuition_tts_speed") || "1");
    return Number.isFinite(v) && v >= 0.5 && v <= 2 ? v : 1;
  });
  useEffect(() => {
    try { window.localStorage.setItem("tuition_tts_speed", String(speed)); } catch {/* noop */}
    // Apply immediately if audio is currently loaded
    if (audioElRef.current) { try { audioElRef.current.playbackRate = speed; } catch {/* noop */} }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed]);
  const [recording, setRecording] = useState(false);
  const [recLevel, setRecLevel] = useState(0);  // 0..1 mic level
  const [processing, setProcessing] = useState(""); // "" | "transcribing" | "thinking" | "speaking"
  const [bootError, setBootError] = useState("");
  const [videoPickKey, setVideoPickKey] = useState(0);

  // ── Q&A videos (question → YouTube) picked from a dropdown or matched by voice ──
  const qaVideos = lesson?.video_qa || [];

  function _norm(s) {
    return (s || "").toLowerCase().replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
  }
  // Closest-match a spoken/typed question to a lesson Q&A video. Always returns
  // the best candidate (never null when the list is non-empty).
  function matchQaVideo(text) {
    if (!qaVideos.length) return null;
    const t = _norm(text);
    const tw = new Set(t.split(" ").filter((w) => w.length > 2));
    let best = qaVideos[0], bestScore = -1;
    for (const v of qaVideos) {
      const q = _norm(v.question);
      const qw = [...new Set(q.split(" ").filter((w) => w.length > 2))];
      let overlap = 0;
      for (const w of qw) if (tw.has(w)) overlap += 1;
      let score = overlap / Math.max(1, qw.length);
      if (t && (t.includes(q) || q.includes(t))) score += 0.5;
      if (score > bestScore) { bestScore = score; best = v; }
    }
    return best;
  }

  function playQaVideo(entry, { spoken = false } = {}) {
    if (!entry) return;
    setMessages((prev) => [...prev, {
      role: "assistant",
      content: spoken ? `Here's a video for "${entry.question}" — tap play to watch.` : entry.question,
      videoUrl: entry.video_url,
    }]);
  }

  function handlePickVideo(qid) {
    const entry = qaVideos.find((v) => v.id === qid);
    playQaVideo(entry);
    setVideoPickKey((k) => k + 1); // reset the dropdown back to its placeholder
  }
  const [chatInput, setChatInput] = useState("");
  const [kickedOff, setKickedOff] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [attachedImage, setAttachedImage] = useState(null);

  const scrollerRef = useRef(null);
  const mediaRecRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const audioElRef = useRef(null);
  const lastReplyRef = useRef(null);
  const kickedOffRef = useRef(false);
  const primedRef = useRef(false);

  // A tiny silent clip used to "unlock" audio playback on the student's first
  // tap. Mobile browsers only allow programmatic audio.play() if the element
  // was already started inside a user gesture — priming this ONE persistent
  // element makes every later tutor reply play instantly and reliably.
  const SILENT_CLIP = "data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==";
  const primeAudio = () => {
    const el = audioElRef.current;
    if (!el || primedRef.current) return;
    try {
      el.muted = true;
      el.src = SILENT_CLIP;
      const p = el.play();
      const done = () => { try { el.pause(); el.currentTime = 0; } catch {/* noop */} el.muted = false; primedRef.current = true; };
      if (p && typeof p.then === "function") p.then(done).catch(() => { el.muted = false; });
      else done();
    } catch { try { el.muted = false; } catch {/* noop */} }
  };
  // Also prime on the very first tap anywhere (covers the auto-kickoff greeting).
  useEffect(() => {
    const h = () => primeAudio();
    window.addEventListener("pointerdown", h, { once: true });
    window.addEventListener("touchstart", h, { once: true });
    return () => { window.removeEventListener("pointerdown", h); window.removeEventListener("touchstart", h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Audit: track time + question count for this lesson session ──
  // The hook below opens a `tuition_sessions` doc on mount, ticks a heartbeat
  // every 20s while the tab is visible + the student isn't idle, and fires
  // an end-beacon on unload. `recordQuestion` is invoked from the chat send
  // handlers so we get an accurate question_count without touching the
  // existing /tuition/voice/reply payload.
  const { recordQuestion } = useTuitionSession(lessonId);

  // ── Load subject + lesson title ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [subRes, lesRes] = await Promise.all([
          api.get("/tuition/subjects"),
          api.get("/tuition/lessons", { params: { subject_id: subjectId } }),
        ]);
        if (!alive) return;
        setSubject(subRes.data.find((s) => s.id === subjectId) || null);
        setLesson(lesRes.data.find((l) => l.id === lessonId) || null);
      } catch (e) {
        if (alive) setBootError(apiErr(e.response?.data?.detail) || "Could not load lesson.");
      }
    })();
    return () => { alive = false; };
  }, [subjectId, lessonId]);

  // ── Auto-scroll ──
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, processing]);

  // ── Kick off teaching on mount ──
  useEffect(() => {
    if (kickedOffRef.current || !lessonId) return;
    kickedOffRef.current = true;
    setKickedOff(true);
    void runReply({ history: [] });
    return () => stopRecording(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  // ─────────────────────────────────────────────────────────────────────
  // Core: send conversation history → get spoken reply
  // ─────────────────────────────────────────────────────────────────────
  async function runReply({ history, image }) {
    // Remember the last request so the student can retry if it stalls/fails.
    lastReplyRef.current = { history, image };
    setProcessing("thinking");
    setBootError("");
    // Client-side safety net: abort if the tutor stalls past 45s (matching the
    // backend timeout) and offer a friendly retry instead of a blank wait.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 45000);
    // Placeholder assistant bubble we stream tokens into (identified by id so
    // rapid delta updates always target the right message).
    const msgId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
    const patch = (p) => setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, ...p } : x)));
    setMessages((m) => [...m, { id: msgId, role: "assistant", content: "", ttsUrl: null, teacherName: "" }]);
    try {
      const token = localStorage.getItem("sa_student_token");
      const resp = await fetch(`${API}/api/tuition/voice/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ lesson_id: lessonId, messages: history, image: image || null }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        let detail = "Couldn't reach the tutor. Tap Retry to try again.";
        try { const j = await resp.json(); detail = apiErr(j.detail) || detail; } catch { /* non-json */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", full = "", meta = null, streamErr = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const raw = line.slice(5).trim();
          if (!raw || raw === "[DONE]") continue;
          let obj; try { obj = JSON.parse(raw); } catch { continue; }
          if (obj.delta) {
            full += obj.delta;
            patch({ content: full });
          } else if (obj.meta) {
            meta = obj.meta;
          } else if (obj.error) {
            streamErr = obj.error;
          }
        }
      }
      if (streamErr) throw new Error(streamErr);
      // Fetch the spoken audio as a Blob (with the auth header) and play it from
      // a local object URL. Direct <audio src=streaming-endpoint> is unreliable
      // on phones (can't send the auth header, iOS needs byte-range support, and
      // the browser's preflight can consume a single-use token) — a fully
      // downloaded blob plays reliably everywhere.
      patch({ teacherName: (meta && meta.teacher_name) || "" });
      setProcessing("");
      if (meta && meta.tts_token) {
        // Stream the audio natively (token in the URL) so playback starts on the
        // FIRST bytes instead of waiting for the whole clip to download — this is
        // the low-latency path. The persistent element is already unlocked, so it
        // autoplays reliably; the Bubble retries once if the stream errors.
        patch({ ttsUrl: `${API}/api/tuition/voice/tts?token=${encodeURIComponent(meta.tts_token)}` });
      }
    } catch (e) {
      // Drop the empty placeholder bubble on failure.
      setMessages((m) => m.filter((x) => !(x.id === msgId && !x.content)));
      const aborted = controller.signal.aborted || e.name === "AbortError";
      const detail = aborted
        ? "The tutor is taking longer than usual — it may be busy. Tap Retry to try again."
        : (e.message || "Couldn't reach the tutor. Tap Retry to try again.");
      setBootError(detail);
      if (!aborted) toast.error(detail);
      setProcessing("");
    } finally {
      clearTimeout(timer);
    }
  }

  function retryReply() {
    if (lastReplyRef.current) void runReply(lastReplyRef.current);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Voice in: press-and-hold mic
  // ─────────────────────────────────────────────────────────────────────
  async function startRecording() {
    if (recording || processing) return;
    primeAudio();
    // Pause any in-flight tutor audio so the student can interrupt cleanly.
    try { audioElRef.current?.pause(); } catch { /* noop */ }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Visual waveform via AudioContext analyser
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) {
          const v = Math.abs(data[i] - 128) / 128;
          if (v > peak) peak = v;
        }
        setRecLevel(peak);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        try {
          stream.getTracks().forEach((t) => t.stop());
          cancelAnimationFrame(rafRef.current);
          try { await ctx.close(); } catch { /* noop */ }
          setRecLevel(0);
          if (!chunks.length) { setProcessing(""); return; }
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          await sendAudio(blob);
        } catch (err) {
          toast.error(err.message || "Recording finalize failed.");
          setProcessing("");
        }
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      const msg = err?.name === "NotAllowedError"
        ? "Microphone access denied. Allow it in your browser to use voice mode."
        : (err?.message || "Could not start recording.");
      toast.error(msg);
    }
  }

  function stopRecording(silent = false) {
    const rec = mediaRecRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* noop */ }
    }
    mediaRecRef.current = null;
    setRecording(false);
    if (!silent) setProcessing("transcribing");
  }

  async function sendAudio(blob) {
    try {
      const form = new FormData();
      form.append("audio", blob, "doubt.webm");
      const { data } = await api.post("/tuition/voice/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data.text || "").trim();
      if (!text) {
        toast.message("Didn't catch that — try again?");
        setProcessing("");
        return;
      }
      const next = [...messages, { role: "user", content: text }];
      setMessages(next);
      recordQuestion();
      // If this lesson has Q&A videos, an oral question also plays the closest
      // matching video (in addition to the tutor's spoken answer).
      const vid = matchQaVideo(text);
      if (vid) playQaVideo(vid, { spoken: true });
      await runReply({ history: next });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not transcribe audio.");
      setProcessing("");
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Text fallback
  // ─────────────────────────────────────────────────────────────────────
  async function sendText() {
    const trimmed = chatInput.trim();
    if ((!trimmed && !attachedImage) || processing) return;
    primeAudio();
    setChatInput("");
    const img = attachedImage;
    setAttachedImage(null);
    const next = [...messages, { role: "user", content: trimmed, image: img }];
    setMessages(next);
    recordQuestion();
    await runReply({ history: next.map((m) => ({ role: m.role, content: m.content })), image: img });
  }

  const [picBusy, setPicBusy] = useState(false);

  // Redraw / Visual-quiz actions that appear under the tutor's latest sketch.
  async function askTutor(text) {
    if (processing) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    recordQuestion();
    await runReply({ history: next.map((m) => ({ role: m.role, content: m.content })) });
  }
  const redrawSketch = (mode) => askTutor(
    mode === "simpler"
      ? "Please redraw that last sketch again, but MUCH SIMPLER and cleaner with fewer details, larger shapes and only the most important labels — easy for a younger learner."
      : "Please redraw that last sketch again, but BIGGER and clearer, with neat, easy-to-read labels and a little more detail."
  );
  const quizFromSketch = () => askTutor(
    "Turn that last diagram into a quick 'Label the parts' practice for me: redraw the SAME sketch but replace each label with a number (1, 2, 3 …) placed next to the part, then list the questions like 'What is part 1?  What is part 2?  …'. Put the correct answers at the very END under a heading 'Answers (try first, then peek!)'."
  );

  async function generatePicture() {
    // ── Replaced ──────────────────────────────────────────────────────
    // The "Show me with a picture" AI image was swapped for an audio-podcast
    // player so every student can listen to the lesson recording the school
    // uploaded. Handler kept as a thin shim so old bindings still work.
    return playAudioPodcast();
  }

  async function playAudioPodcast() {
    if (!lessonId) { toast.error("Open a specific lesson first."); return; }
    if (picBusy) return;
    setPicBusy(true);
    try {
      // Cheap probe: audio-token 404s if the school hasn't uploaded a podcast yet.
      await api.get(`/tuition/lessons/${lessonId}/audio-token`);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Here's the audio podcast for this lesson — tap play:", audioLessonId: lessonId },
      ]);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 404
        ? "The audio podcast for this lesson hasn't been uploaded yet."
        : (apiErr(e.response?.data?.detail) || "Couldn't load the podcast."));
    } finally {
      setPicBusy(false);
    }
  }

  const headerTitle = useMemo(() => lesson?.title || "Loading lesson…", [lesson]);
  const headerSub = useMemo(
    () => (subject?.name ? `${subject.name} · Class ${subject.class_name || "—"}` : ""),
    [subject],
  );

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────
  const chatInner = (
    <>
        {/* Header */}
        <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => navigate("/student/tuitions")}
            data-testid="tchat-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="h-9 w-9 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center shrink-0">
            <BookOpen className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Live tutor</div>
            <h1
              className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 truncate"
              style={{ fontFamily: "Outfit" }}
              data-testid="tchat-lesson-title"
            >
              {headerTitle}
            </h1>
            <div className="text-xs text-slate-500">{headerSub}</div>
          </div>
          <SpeedPicker speed={speed} setSpeed={setSpeed} />
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-9 w-9 p-0 shrink-0 lg:hidden"
            onClick={() => setPdfOpen(true)}
            title="Open lesson PDF"
            data-testid="tchat-open-pdf-mobile"
          >
            <BookOpen className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-9 w-9 p-0 shrink-0"
            onClick={() => setFullscreen((v) => !v)}
            title={fullscreen ? "Exit full screen" : "Full screen"}
            data-testid="tchat-fullscreen-toggle"
          >
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        {/* Messages */}
        <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto py-4 space-y-4" data-testid="tchat-messages">
          {/* One persistent, gesture-primed audio element for all tutor replies. */}
          <audio ref={audioElRef} className="hidden" preload="auto" playsInline data-testid="tchat-tts-audio" />
          {messages.map((m, i) => (
            <Bubble
              key={i}
              role={m.role}
              content={m.content}
              image={m.image}
              ttsUrl={m.ttsUrl}
              picUrl={m.picUrl}
              videoUrl={m.videoUrl}
              videoLessonId={m.videoLessonId}
              audioLessonId={m.audioLessonId}
              teacherName={m.teacherName}
              isLast={i === messages.length - 1}
              audioRef={i === messages.length - 1 && m.role === "assistant" ? audioElRef : null}
              speed={speed}
              onRedraw={redrawSketch}
              onQuiz={quizFromSketch}
              sketchActionsDisabled={!!processing}
              onAudioEnded={() => setProcessing("")}
            />
          ))}
          {processing && (
            <StatusPill kind={processing} />
          )}
          {bootError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 px-4 py-3 text-sm flex items-center justify-between gap-3" data-testid="tchat-error">
              <span>{bootError}</span>
              {lastReplyRef.current && !processing && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={retryReply}
                  className="shrink-0 rounded-full border-rose-300 text-rose-700 hover:bg-rose-100 dark:border-rose-700 dark:text-rose-200"
                  data-testid="tchat-retry-btn"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Quick actions (picture / video — coming soon) */}
        <div className="flex items-center gap-2 pb-3">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={playAudioPodcast}
            disabled={picBusy || !!processing}
            data-testid="tchat-audio-podcast"
          >
            {picBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Headphones className="h-4 w-4 mr-1.5" />}
            {picBusy ? "Loading…" : "Audio Podcast"}
          </Button>
          {qaVideos.length > 0 ? (
            <div className="flex-1 min-w-[180px] max-w-xs">
              <Select key={videoPickKey} onValueChange={handlePickVideo}>
                <SelectTrigger className="rounded-full h-9" data-testid="tchat-video-question-select">
                  <VideoIcon className="h-4 w-4 mr-1.5 text-rose-500 shrink-0" />
                  <SelectValue placeholder="Watch a video answer…" />
                </SelectTrigger>
                <SelectContent className="max-w-sm">
                  {qaVideos.map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`tchat-video-q-${v.id}`}>
                      {v.question}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => {
                if (lesson?.video_file_id) {
                  setMessages((prev) => [...prev, {
                    role: "assistant",
                    content: "Here's a short video for this lesson — tap play to watch.",
                    videoLessonId: lesson.id,
                  }]);
                } else if (lesson?.video_url) {
                  setMessages((prev) => [...prev, {
                    role: "assistant",
                    content: "Here's a short video for this lesson — tap play to watch.",
                    videoUrl: lesson.video_url,
                  }]);
                } else {
                  toast.message("Video is being prepared", { description: "A short video for this lesson will be available soon." });
                }
              }}
              data-testid="tchat-show-video"
            >
              <VideoIcon className="h-4 w-4 mr-1.5" /> Show me with a short video
            </Button>
          )}
        </div>

        {/* Composer — unified chat + voice bar */}
        <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
          <UnifiedComposer
            value={chatInput}
            onChange={setChatInput}
            onSend={sendText}
            busy={!!processing}
            image={attachedImage}
            onImage={setAttachedImage}
            onClearImage={() => setAttachedImage(null)}
            recording={recording}
            level={recLevel}
            onStartRec={startRecording}
            onStopRec={() => stopRecording(false)}
          />
        </div>
    </>
  );

  const pdfUrl = lessonId ? `${API}/api/tuition/lessons/${lessonId}/pdf` : null;
  const pdfPanel = (
    <div className="hidden lg:flex flex-col flex-1 min-w-0" data-testid="tchat-pdf-panel">
      <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div className="h-9 w-9 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center shrink-0">
          <BookOpen className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Lesson</div>
          <h1
            className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 truncate"
            style={{ fontFamily: "Outfit" }}
            data-testid="tchat-pdf-title"
          >
            {headerTitle}
          </h1>
          <div className="text-xs text-slate-500">{headerSub}</div>
        </div>
      </div>
      <div className="flex-1 min-h-0 pt-4">
        {pdfUrl ? (
          <PdfViewer url={pdfUrl} />
        ) : (
          <div className="p-6 text-sm text-slate-400">No PDF available for this lesson.</div>
        )}
      </div>
    </div>
  );

  // Single stable tree so toggling fullscreen never unmounts the chat/audio
  // element (which would stop TTS playback). Only the wrapper class changes.
  return (
    <StudentLayout>
      <div
        className={
          fullscreen
            ? "fixed inset-0 z-[60] bg-white dark:bg-slate-900 flex gap-4 px-4 sm:px-6 py-4"
            : "h-[calc(100vh-7rem)] flex gap-4"
        }
        data-testid={fullscreen ? "tchat-fullscreen" : "tchat-normal"}
      >
        <div className="flex flex-col flex-1 min-w-0">
          {chatInner}
        </div>
        {pdfPanel}
      </div>

      {/* Mobile: full-screen lesson PDF overlay */}
      {pdfOpen && (
        <div className="fixed inset-0 z-[70] bg-white dark:bg-slate-900 flex flex-col lg:hidden" data-testid="tchat-pdf-overlay">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
            <div className="h-9 w-9 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center shrink-0">
              <BookOpen className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Lesson</div>
              <div className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ fontFamily: "Outfit" }}>
                {headerTitle}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full h-9 w-9 p-0 shrink-0"
              onClick={() => setPdfOpen(false)}
              data-testid="tchat-pdf-overlay-close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 p-2">
            {pdfUrl ? <PdfViewer url={pdfUrl} /> : <div className="p-6 text-sm text-slate-400">No PDF available for this lesson.</div>}
          </div>
        </div>
      )}
    </StudentLayout>
  );
}
